import { TableColumnsConfig } from "shared/components/UniversalTable/types";
import type {
  UniversalFilterSection,
  UniversalGroupByOption,
} from "shared/components/UniversalTable/components/UniversalTableSearch";
import { RentalContract } from "shared/types/rental-contracts";

/**
 * Indexation table column configuration
 *
 * Defines the 9 columns for the rental contracts indexation overview:
 * - Checkbox (for bulk selection)
 * - Contract ID
 * - Unit ID
 * - Building ID
 * - Contract Contacts (Huurders)
 * - Start Date
 * - Active Price
 * - Active Provision
 * - Actions
 */
export const INDEXATION_COLUMNS_CONFIG: TableColumnsConfig = {
  columns: [
    {
      id: "checkbox",
      label: "",
      minWidth: 40,
      defaultWeight: 0,
      className: "flex justify-center",
    },
    {
      id: "status",
      label: "",
      minWidth: 40,
      defaultWeight: 0,
      className: "flex justify-center",
    },
    {
      id: "contractId",
      label: "Contract ID",
      minWidth: 100,
      defaultWeight: 1.5,
      sortKey: "id",
    },
    {
      id: "unitId",
      label: "Unit",
      minWidth: 120,
      defaultWeight: 2,
      sortKey: "unit.id",
    },
    {
      id: "unitType",
      label: "Type",
      minWidth: 100,
      defaultWeight: 1.5,
    },
    {
      id: "contractContacts",
      label: "Huurders",
      minWidth: 180,
      defaultWeight: 3,
    },
    {
      id: "startDate",
      label: "Startdatum",
      minWidth: 150,
      defaultWeight: 1.5,
      sortKey: "startDate",
    },
    {
      id: "signDate",
      label: "Ondertekeningsdatum",
      minWidth: 120,
      defaultWeight: 1.5,
    },
    {
      id: "lastIndexationDate",
      label: "Laatste indexatie",
      minWidth: 150,
      defaultWeight: 2,
    },
    {
      id: "initPrice",
      label: "Basishuurprijs",
      minWidth: 120,
      defaultWeight: 1.5,
    },
    {
      id: "activePrice",
      label: "Huidige prijs",
      minWidth: 100,
      defaultWeight: 1.5,
    },
    {
      id: "activeProvision",
      label: "Huidige provisie",
      minWidth: 100,
      defaultWeight: 1.5,
    },
    {
      id: "actions",
      label: "Indexatie",
      minWidth: 140,
      defaultWeight: 2,
    },
    {
      id: "currentEPC",
      label: "EPC",
      minWidth: 80,
      defaultWeight: 1,
    },
    {
      id: "contractFile",
      label: "Contract",
      minWidth: 60,
      defaultWeight: 0.5,
    },
  ],
  localStorageKey: "indexation-contracts-column-widths-v4",
};

/**
 * Extract unique building options from contracts for filter dropdown
 */
export const extractBuildingOptions = (rows: RentalContract[]) => {
  const buildings = new Map();

  rows.forEach((contract) => {
    if (contract.unit?.building) {
      const building = contract.unit.building;
      if (!buildings.has(building.id)) {
        buildings.set(building.id, {
          key: building.id,
          label: `${building.id} - ${building.company?.name || "Onbekend"}`,
        });
      }
    }
  });

  return Array.from(buildings.values());
};

/**
 * Filter sections for indexation table
 *
 * Note: Building filter options will be extracted dynamically from data
 * using extractBuildingOptions function in the component
 */
export const INDEXATION_FILTER_SECTIONS: UniversalFilterSection[] = [
  {
    key: "building",
    label: "Gebouw",
    options: [], // Will be populated dynamically
    color: "moss",
  },
  {
    key: "timeRange",
    label: "Periode",
    options: [
      { key: "past", label: "Verstreken" },
      { key: "urgent", label: "Urgent (< 1 maand)" },
      { key: "upcoming", label: "Binnenkort (1-3 maanden)" },
      { key: "future", label: "Toekomstig (> 3 maanden)" },
    ],
    color: "yellow",
  },
  {
    key: "indexationStatus",
    label: "Indexatie status",
    options: [
      { key: "urgent", label: "Moet geïndexeerd worden" },
      { key: "notUrgent", label: "Hoeft niet geïndexeerd te worden" },
    ],
    color: "brick",
  },
];

/**
 * Group by options for indexation table
 */
export const INDEXATION_GROUPBY_OPTIONS: UniversalGroupByOption[] = [
  { key: "none", label: "Geen groepering" },
  { key: "building", label: "Per gebouw" },
];
